import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, CheckCircle, MapPin, Award } from 'lucide-react';
import SEO from '../components/SEO';

const AboutPage = () => {
    const servicesList = [
        "Channel Letters", "Monument Signs", "Illuminated Signs",
        "Digital Signs", "Neon Signs", "Vehicle Graphics & Lettering",
        "Window Graphics", "Wall Graphics", "Real Estate Signs",
        "Banner Printing", "Safety & Traffic Signs"
    ];

    const whyChooseUs = [
        "10+ Years of Experience", "Professional Design & Installation",
        "High-Quality Materials", "Custom Sign Solutions",
        "Reliable Customer Support", "Fast & Professional Service"
    ];

    // Animation variants for staggered list reveals
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    };

    return (
        <>

        <SEO
        title="About A2Z Signs | Trusted Sign Company in Upland CA USA"
        description="A2Z Signs is a trusted commercial signage company based in Upland, CA with 10+ years of experience. We design, fabricate & install custom signs for businesses across the USA."
        keywords="trusted commercial signage experts USA"
      />

        <div className="pt-32 pb-24 min-h-screen bg-brand-light overflow-hidden">

            {/* Header Section */}
            <div className="container mx-auto px-6 md:px-12 mb-20">
                <div className="flex flex-col lg:flex-row items-center gap-12">

                    {/* Text Half */}
                    <div className="lg:w-1/2 text-center lg:text-left">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full bg-brand-blue/10 text-brand-blue font-bold tracking-wide text-sm"
                        >
                            <Award size={18} /> Over 10 Years of Experience
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-4xl md:text-6xl font-heading font-extrabold text-brand-dark mb-6 leading-tight"
                        >
                            About <span className="text-brand-blue">A2Z Signs</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8"
                        >
                            Welcome to A2Z Signs, your trusted custom sign company in Upland, California.
                            We provide high-quality signage solutions designed to help businesses improve visibility, branding, and customer engagement.
                        </motion.p>
                    </div>

                    {/* Image Half */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="lg:w-1/2 relative"
                    >
                        <div className="absolute inset-0 bg-brand-cyan rounded-3xl translate-x-4 translate-y-4 opacity-20"></div>
                        <img
                            src="/images/workshop.png"
                            alt="Signage Workshop"
                            className="relative z-10 rounded-3xl shadow-2xl object-cover w-full h-[400px]"
                        />
                    </motion.div>
                </div>
            </div>

            {/* Mission & Vision Split Section */}
            <div className="container mx-auto px-6 md:px-12 mb-32 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Mission Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6 }}
                        className="bg-white p-10 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.05)] border-t-4 border-brand-blue relative overflow-hidden group"
                    >
                        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-blue/5 rounded-bl-full -mr-4 -mt-4 transition-transform duration-500 group-hover:scale-125"></div>
                        <Target className="text-brand-blue mb-6 relative z-10" size={48} />
                        <h3 className="text-3xl font-heading font-bold text-brand-dark mb-4 relative z-10">Our Mission</h3>
                        <p className="text-gray-600 leading-relaxed text-lg relative z-10">
                            Our mission is to provide high-quality custom signage solutions that help businesses improve their brand visibility, attract customers, and grow successfully with professional and creative sign services.
                        </p>
                    </motion.div>

                    {/* Vision Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="bg-brand-dark p-10 rounded-3xl shadow-xl border-t-4 border-brand-cyan relative overflow-hidden group text-white"
                    >
                        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-cyan/10 rounded-bl-full -mr-4 -mt-4 transition-transform duration-500 group-hover:scale-125"></div>
                        <Eye className="text-brand-cyan mb-6 relative z-10" size={48} />
                        <h3 className="text-3xl font-heading font-bold mb-4 relative z-10">Our Vision</h3>
                        <p className="text-gray-300 leading-relaxed text-lg relative z-10">
                            Our vision is to become a trusted signage company known for quality workmanship, innovative designs, and excellent customer service across California.
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* The "Who We Are" & Why Choose Us Section */}
            <div className="bg-white py-24 border-y border-gray-100">
                <div className="container mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

                        {/* Image Grid Left Side */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="grid grid-cols-2 gap-4"
                        >
                            <img
                                src="/images/design-process.png"
                                alt="Installation"
                                className="w-full h-64 object-cover rounded-2xl shadow-lg mt-8 bg-gray-100"
                            />
                            <img
                                src="/images/installation.png"
                                alt="Design Process"
                                className="w-full h-64 object-cover rounded-2xl shadow-lg bg-gray-100"
                            />
                        </motion.div>

                        {/* Text Content */}
                        <div>
                            <motion.h2
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="text-4xl font-heading font-bold text-brand-dark mb-6"
                            >
                                Built on <span className="text-brand-cyan">Quality & Trust</span>
                            </motion.h2>

                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                className="text-gray-600 mb-6 text-lg"
                            >
                                At A2Z Signs, we specialize in professional sign design, custom graphics, and sign installation services for businesses of all sizes. Our goal is to deliver creative, durable, and high-quality signage that helps our clients stand out in today's competitive market.
                            </motion.p>

                            <motion.h3
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                className="text-2xl font-bold text-brand-dark mb-6 mt-8"
                            >
                                Why Customers Choose A2Z Signs:
                            </motion.h3>

                            {/* Staggered List Animation */}
                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                            >
                                {whyChooseUs.map((reason, index) => (
                                    <motion.div key={index} variants={itemVariants} className="flex items-center gap-3 bg-brand-light p-3 rounded-lg border border-gray-100">
                                        <CheckCircle className="text-brand-cyan flex-shrink-0" size={20} />
                                        <span className="font-semibold text-gray-700">{reason}</span>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Services List Display */}
            <div className="container mx-auto px-6 md:px-12 mt-24 mb-16 text-center">
                <motion.h3
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-3xl font-heading font-bold text-brand-dark mb-10"
                >
                    We offer a wide range of signage services, including:
                </motion.h3>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="flex flex-wrap gap-4 justify-center max-w-5xl mx-auto"
                >
                    {servicesList.map((service, index) => (
                        <motion.span
                            key={index}
                            variants={itemVariants}
                            whileHover={{ scale: 1.05, y: -2 }}
                            className="px-6 py-3 bg-white border border-gray-200 text-brand-blue font-bold rounded-full shadow-sm hover:border-brand-cyan hover:shadow-md transition-all duration-300 cursor-default"
                        >
                            {service}
                        </motion.span>
                    ))}
                </motion.div>
            </div>

            {/* CTA Footer for About Page */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="container mx-auto px-6 md:px-12 mt-24 mb-10 text-center"
            >
                <div className="bg-brand-dark rounded-3xl p-12 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-brand-cyan opacity-20 rounded-full blur-[80px] -mr-20 -mt-20"></div>
                    <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-8 relative z-10">
                        Let us help you start your next signage project.
                    </h2>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-6 relative z-10">
                        <div className="flex items-center gap-3 text-lg font-bold text-brand-dark bg-brand-cyan px-6 py-4 rounded-full shadow-lg">
                            <MapPin size={24} />
                            1302 Monte Vista Ave #15, Upland, CA 91786
                        </div>
                        <button className="px-8 py-4 bg-transparent border-2 border-brand-cyan text-white font-bold rounded-full hover:bg-white hover:text-brand-dark hover:border-white transition-all shadow-lg">
                            Contact Us Today
                        </button>
                    </div>
                </div>
            </motion.div>

        </div>
        </>
    );
};

export default AboutPage;